import React, { useState, useEffect } from 'react';
import './CreateAppPopup.css';
import { api } from '../api/robyBackend';

const COLORS = [
  '#4A4FFF', // Blau-Violett
  '#3DDC97', // Mintgrün
  '#FF6B6B', // Koralle
  '#FFD166', // Goldgelb
  '#9D4EDD', // Soft Yellow
  '#D4A5A5', // Lila
  '#9B786F', // Warm Brown
  '#A8A8A8'  // Neutral Gray
];

const CreateAppPopup = ({ isOpen, onClose, onSubmit, editMode = false, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    color: COLORS[0],
    access: 'private',
    files: [],
    isDragging: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accessGroups, setAccessGroups] = useState([]);
  const [loadingAccessGroups, setLoadingAccessGroups] = useState(false);
  // 🚩 FLAG: Custom System Prompt Feature
  const [systemPrompt, setSystemPrompt] = useState('');
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // Neue States für bestehende Dateien
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Berechtigungsgruppen von Django abrufen
  const fetchAccessGroups = async () => {
    setLoadingAccessGroups(true);
    try {
      const response = await api.get('/database/access-groups'); // oder passenden Endpoint
      const groups = response.data || [];
      
      // Falls der Server leere Liste zurückgibt, Fallback-Optionen verwenden
      if (groups.length === 0) {
        setAccessGroups([
          { id: 'private', name: 'Nur für mich', description: 'Privater Zugriff' },
          { id: 'public', name: 'Teilen mit Kollegen', description: 'Öffentlicher Zugriff' }
        ]);
      } else {
        setAccessGroups(groups);
      }
    } catch (error) {
      console.error('Error fetching access groups:', error);
      // Fallback bei Fehler
      setAccessGroups([
        { id: 'private', name: 'Nur für mich', description: 'Privater Zugriff' },
        { id: 'public', name: 'Teilen mit Kollegen', description: 'Öffentlicher Zugriff' }
      ]);
    } finally {
      setLoadingAccessGroups(false);
    }
  };

  // Bestehende Dateien laden (nur im Edit-Modus)
  const fetchExistingFiles = async (appId) => {
    if (!editMode || !appId) return;

    setLoadingFiles(true);
    try {
      const response = await api.get(`/database/apps/${appId}/files`);
      setExistingFiles(response.data.files || []);
    } catch (error) {
      console.error('Fehler beim Laden der Dateien:', error);
      setExistingFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  // 🚩 FLAG: Custom System Prompt Feature – System Prompt laden (nur im Edit-Modus)
  const fetchSystemPrompt = async (appId) => {
    if (!editMode || !appId) return;
    try {
      const response = await api.get(`/database/apps/${appId}/system-prompt`);
      const prompt = response.data.system_prompt || '';
      if (prompt) {
        setSystemPrompt(prompt);
        // bleibt eingeklappt – User öffnet manuell
      }
    } catch (error) {
      console.error('Fehler beim Laden des System Prompts:', error);
    }
  };

  // Access Groups beim ersten Laden abrufen
  useEffect(() => {
    if (isOpen && accessGroups.length === 0) {
      fetchAccessGroups();
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    validateAndSetFiles(selectedFiles);
    // Input zurücksetzen, damit gleiche Dateien erneut gewählt werden können
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, isDragging: false }));
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndSetFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, isDragging: true }));
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, isDragging: false }));
  };

  const validateAndSetFiles = (selectedFiles) => {
    if (selectedFiles.length === 0) return;

    // Nur PDF, Word und Excel erlauben
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const invalidFiles = selectedFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      alert('Nur PDF-, Word- und Excel-Dateien sind erlaubt');
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > 30 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert('Dateien dürfen maximal 30MB groß sein');
      return;
    }

    // GEÄNDERT: Dateien anhängen statt ersetzen
    setFormData(prev => ({ ...prev, files: [...prev.files, ...selectedFiles] }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Ein Agent braucht entweder einen individuellen System Prompt ODER mindestens ein Dokument
  const totalFileCount = formData.files.length + (editMode ? existingFiles.length : 0);
  const hasPromptOrFiles = Boolean(systemPrompt.trim()) || totalFileCount > 0;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!formData.name.trim()) {
      alert('Bitte geben Sie einen Namen für den Agenten ein.');
      return;
    }

    // Validation: Entweder ein System Prompt ODER mindestens eine Datei muss vorhanden sein
    if (!hasPromptOrFiles) {
      alert('Bitte hinterlegen Sie einen System Prompt oder laden Sie mindestens eine Datei hoch.');
      if (!systemPrompt.trim()) setShowSystemPrompt(true);
      return;
    }

    setIsSubmitting(true);
    
  try {

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('color', formData.color);
    submitData.append('access', formData.access);
    // 🚩 FLAG: Custom System Prompt Feature
    if (systemPrompt.trim()) {
      submitData.append('system_prompt', systemPrompt.trim());
    }

    // Neue Dateien hinzufügen (immer als Array behandeln)
    const filesToSend = Array.isArray(formData && formData.files)
      ? formData.files
      : formData && formData.files
        ? [formData.files]
        : [];
    if (filesToSend && Array.isArray(filesToSend)) {
      filesToSend.forEach((file) => {
        submitData.append('files', file);
      });
    }

    // Zu löschende Dateien hinzufügen (nur im Edit-Modus)
    if (editMode && filesToRemove.length > 0) {
      submitData.append('remove_files', JSON.stringify(filesToRemove));
    }
    await onSubmit(submitData, editMode); // FormData für Update
    // ...Reset und Close...
  } catch (error) {
    console.error('Submit Error:', error);
    setIsSubmitting(false);
  }
};

  const resetForm = () => {
    setFormData({
      name: '',
      color: COLORS[0],
      access: accessGroups.length > 0 ? accessGroups[0].id : 'private',
      files: [],
      isDragging: false
    });
    setExistingFiles([]);
    setFilesToRemove([]);
    setIsSubmitting(false);
    // 🚩 FLAG: Custom System Prompt Feature
    setSystemPrompt('');
    setShowSystemPrompt(false);
  };

  // Bestehende Datei entfernen
  const removeExistingFile = (filename) => {
    setFilesToRemove(prev => [...prev, filename]);
    setExistingFiles(prev => prev.filter(f => f.filename !== filename));
  };

  // Entfernte Datei wiederherstellen
  const restoreFile = (filename) => {
    setFilesToRemove(prev => prev.filter(f => f !== filename));
    // Datei wieder zur existingFiles Liste hinzufügen
    if (initialData?._id) {
      fetchExistingFiles(initialData._id);
    }
  };

  // Bestehende Dateien und System Prompt laden beim Öffnen des Popups
  useEffect(() => {
    if (isOpen && editMode && initialData?._id) {
      fetchExistingFiles(initialData._id);
      // 🚩 FLAG: Custom System Prompt Feature – immer erst zurücksetzen, dann laden
      setSystemPrompt('');
      setShowSystemPrompt(false);
      fetchSystemPrompt(initialData._id);
    }
  }, [isOpen, editMode, initialData]);

  // Reset oder Load form data when popup is opened
  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
      setFilesToRemove([]); // Reset der zu löschenden Dateien

      if (editMode && initialData) {
        setFormData({
          name: initialData.name || '',
          color: initialData.color || COLORS[0],
          access: initialData.access || 'private',
          files: [], // Neue Dateien können hinzugefügt werden
          isDragging: false
        });
      } else {
        setFormData({
          name: '',
          color: COLORS[0],
          access: 'private',
          files: [],
          isDragging: false
        });
        setExistingFiles([]); // Reset bestehende Dateien
      }
    }
  }, [isOpen, editMode, initialData]);

  // Access-Wert aktualisieren, wenn accessGroups geladen werden (nur wenn noch Default)
  useEffect(() => {
    if (accessGroups.length > 0) {
      setFormData(prev => {
        if (prev.access === 'private' && !editMode) {
          return { ...prev, access: accessGroups[0].id };
        }
        return prev;
      });
    }
  }, [accessGroups, editMode]);

  if (!isOpen) return null;

  return (
    <div className="uploadPopupOverlay">
      <div className="uploadPopup">
        {/* Close Button */}
        <button className="closeButton" onClick={onClose}>
          ×
        </button>

        <h2>{editMode ? 'Agenten bearbeiten' : 'Neuen Agenten erstellen'}</h2>

        {/* Agent Name */}
        <div className="formGroup">
          <label>Name:</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Geben Sie den Namen des Agenten ein..."
            className="textInput"
          />
        </div>

        {/* Color Picker */}
        <div className="formGroup">
          <label>Farbe:</label>
          <div className="colorPicker">
            {COLORS.map((color) => (
              <div
                key={color}
                onClick={() => setFormData({ ...formData, color: color })}
                className={`colorOption ${formData.color === color ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* 🚩 FLAG: Custom System Prompt Feature */}
        <div className="formGroup">
          <div className="systemPromptHeader">
            <label>Erweiterte Einstellungen</label>
            <button
              type="button"
              className="systemPromptToggle"
              onClick={() => setShowSystemPrompt(prev => !prev)}
            >
              {showSystemPrompt ? 'Einklappen ▲' : 'System Prompt anpassen ▼'}
            </button>
          </div>
          {showSystemPrompt && (
            <div className="systemPromptGroup">
              <label className="systemPromptLabel">System Prompt:</label>
              <textarea
                className="systemPromptTextarea"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Geben Sie hier den individuellen System Prompt für diesen Agenten ein..."
                rows={6}
              />
            </div>
          )}
        </div>

        {/* CHANGED: Zugriff nur anzeigen, wenn !editMode */}
        {!editMode && (
          <div className="formGroup">
            <label>Zugriff:</label>
            {loadingAccessGroups ? (
              <div className="loading-access">Lade Berechtigungen...</div>
            ) : (
              <select
                value={formData.access}
                onChange={(e) => setFormData({ ...formData, access: e.target.value })}
                className="selectInput"
              >
                {accessGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                    {group.description && ` - ${group.description}`}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Bestehende Dateien (nur im Edit-Modus) */}
        {editMode && (
          <div className="formGroup">
            <label>Bestehende Dateien</label>
            {loadingFiles ? (
              <div className="loading-files">Lade Dateien...</div>
            ) : existingFiles.length > 0 ? (
              <div className="selectedFiles">
                <div className="filesList">
                  {existingFiles.map((file, index) => (
                    <div key={index} className="fileItem">
                      <div className="fileInfo">
                        <div className="fileName">{file.filename}</div>
                        <div className="fileSize">
                          {formatFileSize(file.size)} • {new Date(file.upload_date).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file.filename)}
                        className="removeFileButton"
                        title="Datei entfernen"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Zu löschende Dateien anzeigen */}
                {filesToRemove.length > 0 && (
                  <div className="removedFiles">
                    <h4>Zu löschende Dateien:</h4>
                    <div className="filesList">
                      {filesToRemove.map((filename, index) => (
                        <div key={index} className="fileItem removedFileItem">
                          <div className="fileInfo">
                            <div className="fileName removedFileName">{filename}</div>
                            <div className="fileSize">Wird beim Speichern gelöscht</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => restoreFile(filename)}
                            className="restoreFileButton"
                            title="Wiederherstellen"
                          >
                            ↶
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="noFiles">Keine Dateien hochgeladen</div>
            )}
          </div>
        )}

        {/* File Upload Area für neue Dateien */}
        <div className="formGroup">
          <label>
            {editMode ? 'Neue Dateien hinzufügen (optional)' : 'Dateien hochladen (optional)'}
          </label>
          <div className="uploadHint">
            Ein Agent benötigt entweder einen individuellen System Prompt oder mindestens
            ein Dokument. Ohne hinterlegte Dokumente stehen im Chat Datei-Upload und Mikrofon zur
            Verfügung.
          </div>
          <div
            className={`uploadBox ${formData.isDragging ? 'dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <input
              id="fileInput"
              type="file"
              multiple
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
            />
            
            <div className="uploadIcon">📁</div>
            
            {formData.files.length === 0 ? (
              <>
                <div className="uploadBoxText">
                  {editMode 
                    ? 'Neue Dateien hinzufügen (optional)' 
                    : 'Dateien hierher ziehen oder klicken zum Auswählen'
                  }
                </div>
                <div className="uploadBoxSubtext">
                  (max. 30MB, PDF, Word, Excel)
                </div>
              </>
            ) : (
              <div className="selectedFileName">
                {formData.files.length} Datei(en) ausgewählt
              </div>
            )}
          </div>
        </div>

        {/* Selected Files List (neue Dateien) */}
        {formData.files.length > 0 && (
          <div className="selectedFiles">
            <h4>Neue Dateien:</h4>
            <div className="filesList">
              {Array.from(formData.files).map((file, index) => (
                <div key={index} className="fileItem">
                  <div className="fileInfo">
                    <div className="fileName">{file.name}</div>
                    <div className="fileSize">{formatFileSize(file.size)}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className="removeFileButton"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="popupButtons">
          <button className="cancelButton" onClick={onClose}>
            Abbrechen
          </button>
          
          <button
            className="confirmButton"
            onClick={handleSubmit}
            disabled={
              !formData.name.trim() ||
              !hasPromptOrFiles ||
              isSubmitting ||
              loadingAccessGroups ||
              loadingFiles
            }
          >
            {isSubmitting 
              ? (editMode ? 'Wird aktualisiert...' : 'Wird erstellt...') 
              : (editMode ? 'Agenten aktualisieren' : 'Agenten erstellen')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAppPopup;